import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Eye, Loader2, Mail, Save, Send } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { DateInput } from "./ui/date-input";

const pad = (n) => String(n).padStart(2, "0");
const fmtDT = (iso) => { const d = new Date(iso); return isNaN(d) ? iso : d.toLocaleString("el-GR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); };

// Admin → Εργαλεία Συστήματος: Ενημερωτικά email Προέδρων Σύνθεσης «ΑΝΑΧΡΕΩΣΕΙΣ ΔΙΚΑΣΤΩΝ ΠΡΟΔΙΚΑΣΤΙΚΟΥ ΜΕ ΕΙΣΗΓΗΤΕΣ»
// (πόσες ημέρες πριν τη δικάσιμο + ώρα αποστολής, προεπισκόπηση, άμεση αποστολή / δείγμα, ιστορικό)
export const ReassignmentDigestCard = () => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [date, setDate] = useState("");
  const [preview, setPreview] = useState(null);
  const [loadingPrev, setLoadingPrev] = useState(false);
  const [sampleTo, setSampleTo] = useState("");
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);

  const load = () => Promise.all([axios.get(`${API}/reassignment-digest/settings`), axios.get(`${API}/reassignment-digest/history?limit=8`)])
    .then(([a, b]) => { setS({ ...a.data, cc: (a.data.cc_emails || []).join(", ") }); setHistory(b.data?.items || []); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      setSaving(true);
      const r = await axios.put(`${API}/reassignment-digest/settings`, {
        enabled: !!s.enabled, days_before: parseInt(s.days_before, 10) || 0, hour: parseInt(s.hour, 10) || 0, minute: parseInt(s.minute, 10) || 0,
        cc_emails: String(s.cc || "").split(/[,;\s]+/).filter(Boolean),
      });
      toast.success(r.data?.message || "Αποθηκεύτηκε");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Σφάλμα αποθήκευσης"); } finally { setSaving(false); }
  };
  const doPreview = async () => {
    try {
      setLoadingPrev(true);
      const r = await axios.get(`${API}/reassignment-digest/preview${date ? `?date=${date}` : ""}`);
      setPreview(r.data);
      if (!date) setDate(r.data.date);
    } catch (e) { toast.error(e.response?.data?.detail || "Σφάλμα προεπισκόπησης"); } finally { setLoadingPrev(false); }
  };
  const sendNow = async (override) => {
    const to = override ? sampleTo.trim() : "";
    if (override && !to) { toast.error("Δώστε email για το δείγμα"); return; }
    if (!override && !window.confirm(`Αποστολή στους Προέδρους Σύνθεσης για τη δικάσιμο ${(date || preview?.date || "").split("-").reverse().join("/")};`)) return;
    try {
      setSending(true);
      const params = new URLSearchParams(); if (date) params.set("date", date); if (to) params.set("to", to);
      const r = await axios.post(`${API}/reassignment-digest/send-now?${params.toString()}`);
      const failed = (r.data?.results || []).filter(x => !x.sent);
      (failed.length ? toast.warning : toast.success)(`${r.data?.message}${failed.length ? ` — αποτυχίες: ${failed.map(f => `${f.pinakio_label}: ${f.error}`).join("; ")}` : ""}`, { duration: 12000 });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Σφάλμα αποστολής", { duration: 10000 }); } finally { setSending(false); }
  };

  if (!s) return null;
  return (
    <div className="rounded-lg border bg-white p-4 space-y-3" data-testid="reassignment-digest-card">
      <div className="flex items-start gap-2 flex-wrap">
        <Mail className="w-5 h-5 text-orange-700 mt-0.5" />
        <div className="flex-1 min-w-[260px]">
          <h3 className="font-semibold text-sm text-slate-800">Ενημερωτικά email Προέδρων Σύνθεσης — «{s.subject}»</h3>
          <p className="text-xs text-slate-500">Στέλνεται αυτόματα στον/στην Πρόεδρο κάθε Σύνθεσης Πολυμελούς με τις υποθέσεις της δικασίμου που «ΑΠΑΙΤΕΙΤΑΙ ΑΝΑΧΡΕΩΣΗ» + προσυμπληρωμένα υποδείγματα Πράξης Νέου Ορισμού Εισηγητή (Word, ημερομηνία = ημέρα συνεδρίασης).</p>
        </div>
        <label className="flex items-center gap-2 text-xs font-medium" data-testid="digest-enabled-label">
          <Switch checked={!!s.enabled} onCheckedChange={(v) => setS({ ...s, enabled: v })} data-testid="digest-enabled" />
          {s.enabled ? "Ενεργό" : "Ανενεργό"}
        </label>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
        <div>
          <Label className="text-xs">Ημέρες πριν τη δικάσιμο</Label>
          <Input type="number" min="0" max="30" className="h-8" value={s.days_before} onChange={e => setS({ ...s, days_before: e.target.value })} data-testid="digest-days-before" />
        </div>
        <div>
          <Label className="text-xs">Ώρα</Label>
          <Input type="number" min="0" max="23" className="h-8" value={s.hour} onChange={e => setS({ ...s, hour: e.target.value })} data-testid="digest-hour" />
        </div>
        <div>
          <Label className="text-xs">Λεπτά</Label>
          <Input type="number" min="0" max="59" className="h-8" value={s.minute} onChange={e => setS({ ...s, minute: e.target.value })} data-testid="digest-minute" />
        </div>
        <div className="col-span-2 md:col-span-1">
          <Label className="text-xs">CC (προαιρετικά)</Label>
          <Input className="h-8 text-xs" value={s.cc} onChange={e => setS({ ...s, cc: e.target.value })} placeholder="email1, email2" data-testid="digest-cc" />
        </div>
        <Button size="sm" className="h-8 gap-1" onClick={save} disabled={saving} data-testid="digest-save">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}Αποθήκευση
        </Button>
      </div>
      <p className="text-[11px] text-slate-500" data-testid="digest-schedule-summary">
        Προγραμματισμός: {s.enabled ? "κάθε μέρα" : "ΑΝΕΝΕΡΓΟ —"} στις <b>{pad(s.hour)}:{pad(s.minute)}</b> ({s.timezone}) για τις δικασίμους {parseInt(s.days_before, 10) === 0 ? <b>της ίδιας ημέρας</b> : <b>σε {s.days_before} ημέρα/ες</b>}.
      </p>

      <div className="border-t pt-3 flex flex-wrap items-end gap-2">
        <div>
          <Label className="text-xs">Δικάσιμος</Label>
          <DateInput className="h-8 w-40" value={date} onChange={e => setDate(e.target.value)} data-testid="digest-date" />
        </div>
        <Button variant="outline" size="sm" className="h-8 gap-1" onClick={doPreview} disabled={loadingPrev} data-testid="digest-preview-btn">
          {loadingPrev ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}Προεπισκόπηση
        </Button>
        <Button size="sm" className="h-8 gap-1 bg-orange-600 hover:bg-orange-700" onClick={() => sendNow(false)} disabled={sending} data-testid="digest-send-now-btn">
          {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}Αποστολή τώρα στους Προέδρους
        </Button>
        <div className="flex items-end gap-1 ml-auto">
          <div>
            <Label className="text-xs">Δείγμα σε email</Label>
            <Input className="h-8 w-56 text-xs" value={sampleTo} onChange={e => setSampleTo(e.target.value)} placeholder="name@example.com" data-testid="digest-sample-to" />
          </div>
          <Button variant="outline" size="sm" className="h-8 gap-1" onClick={() => sendNow(true)} disabled={sending} data-testid="digest-send-sample-btn">
            <Mail className="w-3.5 h-3.5" />Αποστολή δείγματος
          </Button>
        </div>
      </div>

      {preview && (
        <div className="rounded border bg-slate-50 p-3 text-xs space-y-2" data-testid="digest-preview">
          <div className="flex items-center gap-2 flex-wrap">
            <b>Δικάσιμος {preview.date.split("-").reverse().join("/")}</b>
            <Badge variant="outline" className="border-slate-300">{preview.hearings.length} δικάσιμοι Πολυμελούς με Σύνθεση</Badge>
            <Badge className={`border-0 text-white ${preview.total_needing ? "bg-red-600" : "bg-emerald-600"}`} data-testid="digest-preview-needing">{preview.total_needing} υποθ. ΑΠΑΙΤΕΙΤΑΙ ΑΝΑΧΡΕΩΣΗ</Badge>
            <Badge variant="outline" className="border-orange-300 text-orange-800">{preview.emails_to_send} email</Badge>
            {preview.transport && <Badge variant="outline" className={`${/Resend/.test(preview.transport) ? "border-red-300 text-red-800" : "border-emerald-300 text-emerald-800"}`} data-testid="digest-transport">Αποστολή: {preview.transport}</Badge>}
            {preview.presidents_without_email > 0 && <Badge className="bg-red-600 text-white border-0" data-testid="digest-missing-emails">{preview.presidents_without_email} Πρόεδροι χωρίς email</Badge>}
          </div>
          {!preview.hearings.length && <p className="text-slate-500 italic">Καμία δικάσιμος Πολυμελούς με ανατεθειμένη Σύνθεση για την ημέρα.</p>}
          {preview.hearings.map(h => (
            <details key={h.pinakio_label} className="rounded border bg-white px-2 py-1.5" data-testid={`digest-preview-${h.pinakio_label}`}>
              <summary className="cursor-pointer flex flex-wrap items-center gap-2">
                <span className="font-mono font-semibold">{h.pinakio_label}</span>
                <span>Σύνθεση «{h.composition_label}»</span>
                <span className="text-slate-600">Πρόεδρος: {h.president?.name || "—"} {h.president?.email ? <span className="text-emerald-700">({h.president.email})</span> : <span className="text-red-700 font-semibold">(χωρίς email!)</span>}</span>
                <span className={`ml-auto font-bold ${h.rows.length ? "text-red-700" : "text-emerald-700"}`}>{h.rows.length}/{h.cases_total} ΑΠΑΙΤΕΙΤΑΙ ΑΝΑΧΡΕΩΣΗ</span>
              </summary>
              <pre className="mt-2 whitespace-pre-wrap font-sans text-[11px] text-slate-700 border-t pt-2">{h.body}</pre>
            </details>
          ))}
        </div>
      )}

      {history.length > 0 && (
        <div className="border-t pt-2" data-testid="digest-history">
          <p className="text-[11px] font-semibold text-slate-600 mb-1">Πρόσφατες αποστολές</p>
          <ul className="text-[11px] space-y-0.5">
            {history.map(h => (
              <li key={h.id} className="flex flex-wrap gap-x-2 text-slate-600">
                <span>{fmtDT(h.sent_at)}</span><span className="font-mono">{h.pinakio_label}</span><span>δικ. {h.date?.split("-").reverse().join("/")}</span>
                <span>→ {h.to || "—"}</span><span className="text-slate-400">({h.trigger}{h.override ? " · δείγμα" : ""})</span>
                <span className={h.sent ? "text-emerald-700" : "text-red-700"}>{h.sent ? `OK · ${h.cases_needing} υποθ. · ${h.attachments ?? 0} συν.` : `ΑΠΟΤΥΧΙΑ: ${h.error}`}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ReassignmentDigestCard;
